import { useSelector } from 'react-redux';
import { CART_TYPE } from '@/services/cart/models/cart';
import { TUpdateCartItemQuantityPayload } from '@/services/cart/models/update-cart-quantity';
import { TUpdateCartItemPayload } from '@/services/cart/models/add-item-to-cart';
import { useRef } from 'react';
import { unlmtdCartService } from '@/services/unlmtd/cart';
import { useNotification } from '@/hooks/useNotification';
import { NotificationSeverity } from '@/components/NotifyToast';
import {
	cartLoading,
	cartSuccess,
	cartError,
	clearCart,
} from '@/store/unlmtd/slices/cartSlice';
import { useLencoDispatch } from '@/hooks/useLencoDispatch';

export function useCart() {
	const dispatch = useLencoDispatch();
	const {
		actions: { notifyUser },
	} = useNotification();
	const cartState = useSelector((state: any) => state.unlmtdCart);
	const totalCartItems = cartState?.cart?.data?.totalCartItems || 0;
	const isFetching = useRef(false);

	function getCartApiState() {
		return cartState?.status;
	}

	const fetchCartData = async (query: any = {}) => {
		if (isFetching.current) return;
		isFetching.current = true;
		dispatch(cartLoading());

		try {
			const response = await unlmtdCartService.getCarts(query);
			dispatch(cartSuccess(response.data));
			return response.data;
		} catch (error: any) {
			console.error('Error fetching cart:', error);
			dispatch(cartError(error.toString()));
			throw error;
		} finally {
			isFetching.current = false;
		}
	};

	const addCartItemAction = async (payload: any) => {
		dispatch(cartLoading());

		try {
			const response = await unlmtdCartService.addItemToCart(payload);

			if (response.data?.data?.cart) {
				dispatch(cartSuccess(response?.data?.data?.cart));
			}

			// Fetch the updated cart after adding an item
			await fetchCartData({});

			notifyUser({
				message: 'Product Successfully added to cart',
				severity: NotificationSeverity.SUCCESS,
			});

			return response.data;
		} catch (error: any) {
			console.error('Failed to add item to cart:', error);
			const errorMessage =
				error?.response?.data?.error?.message || 'Failed to add item to cart';

			dispatch(cartError(errorMessage));

			notifyUser({
				message: errorMessage,
				severity: NotificationSeverity.ERROR,
			});

			throw error;
		}
	};

	const removeCartItemAction = async (payload: any) => {
		dispatch(cartLoading());

		try {
			const response = await unlmtdCartService.removeItemFromCart(payload);

			// Update cart state with the response
			if (response.data?.data?.cart) {
				dispatch(cartSuccess(response?.data?.data?.cart));
			}

			// Fetch the updated cart after removing an item
			await fetchCartData({});

			notifyUser({
				message: 'Product Successfully removed from cart',
				severity: NotificationSeverity.SUCCESS,
			});

			return response.data;
		} catch (error: any) {
			console.error('Failed to remove item from cart:', error);
			const errorMessage =
				error?.response?.data?.error?.message ||
				'Failed to remove item from cart';

			dispatch(cartError(errorMessage));

			notifyUser({
				message: errorMessage,
				severity: NotificationSeverity.ERROR,
			});

			throw error;
		}
	};

	const updateQuantityAction = async (
		cartId: number,
		itemId: number,
		cartType: CART_TYPE,
		payload: TUpdateCartItemQuantityPayload
	) => {
		dispatch(cartLoading());

		try {
			const response = await unlmtdCartService.updateCartItemQuantity(
				cartId,
				itemId,
				cartType,
				payload
			);

			// Update cart state with the response
			if (response.data?.data?.cart) {
				dispatch(cartSuccess(response?.data?.data?.cart));
			}

			return { data: { success: true } };
		} catch (error: any) {
			console.error('Failed to update item quantity:', error);
			const errorMessage =
				error?.response?.data?.error?.message ||
				'Failed to update item quantity';

			dispatch(cartError(errorMessage));

			return { data: { success: false, error } };
		}
	};

	const updateCartAction = async (cartId: number, payload: any) => {
		dispatch(cartLoading());

		try {
			const response = await unlmtdCartService.updateCart(cartId, payload);

			// Update cart state with the response
			if (response.data?.data?.cart) {
				dispatch(cartSuccess(response?.data?.data?.cart));
			}

			return { data: { success: true } };
		} catch (error: any) {
			console.error('Failed to update cart:', error);
			const errorMessage =
				error?.response?.data?.error?.message || 'Failed to update cart';

			dispatch(cartError(errorMessage));

			return { data: { success: false, error } };
		}
	};

	const createPaymentAction = async (payload: any) => {
		try {
			const response = await unlmtdCartService.createPayment(payload);
			return response.data;
		} catch (error: any) {
			console.error('Failed to create payment:', error);
			notifyUser({
				message:
					error?.response?.data?.error?.message || 'Failed to create payment',
				severity: NotificationSeverity.ERROR,
			});
			throw error;
		}
	};

	const getServicePacksAction = async (cityPlanId: any) => {
		try {
			const response = await unlmtdCartService.getServicePacks(cityPlanId);
			return response.data;
		} catch (error: any) {
			console.error('Failed to get service packs:', error);
			notifyUser({
				message:
					error?.response?.data?.error?.message ||
					'Failed to get service packs',
				severity: NotificationSeverity.ERROR,
			});
			throw error;
		}
	};

	function clearCartData() {
		dispatch(clearCart());
	}

	return {
		totalCartItems,
		getCartApiState,
		cartActions: {
			fetchCart: fetchCartData,
			addCartItem: addCartItemAction,
			removeCartItem: removeCartItemAction,
			updateQuantity: updateQuantityAction,
			updateCart: updateCartAction,
			createPayment: createPaymentAction,
			getServicePacks: getServicePacksAction,
			clearCart: clearCartData,
		},
		cart: cartState,
	};
}
